import React, { useState } from 'react';
import './login-screen.scss';
import { login, LoginRequest } from '../../utils/api';
import { saveSession } from '../../utils/session';

interface LoginScreenProps {
  onComplete: (userData: { username: string; mobileNumber: string }) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onComplete }) => {
  const [username, setUsername] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ 
    username?: string; 
    mobileNumber?: string; 
    general?: string;
  }>({});

  const validateForm = () => {
    const newErrors: { 
      username?: string; 
      mobileNumber?: string; 
    } = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.trim().length < 2) {
      newErrors.username = 'Username must be at least 2 characters';
    }
    
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\+?[\d\s-()]{8,15}$/.test(mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid mobile number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      // Always send static credentials to backend, regardless of user input
      // Keep all properties to maintain backend compatibility
      const loginRequest: LoginRequest = {
        userName: 'Admin',
        password: 'P@ssw0rd',
        mobile: mobileNumber.trim(),
        email: '', // Keep email field but send empty string
      };
      
      const response = await login(loginRequest);
      
      // Save session data
      saveSession({
        token: response.token,
        userName: response.userName,
        mobile: response.mobile,
        email: response.email,
      });
      
      // Call onComplete with user data
      const userData = {
        username: response.userName,
        mobileNumber: response.mobile,
      };
      
      onComplete(userData);
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (errors.username) {
      setErrors(prev => ({ ...prev, username: undefined }));
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobileNumber(e.target.value);
    if (errors.mobileNumber) {
      setErrors(prev => ({ ...prev, mobileNumber: undefined }));
    }
  };

  return (
    <div className="login-screen">
      <div className="">
        <div className="login-header">
          <div className="">
            <img src="/Ameen-Logo.png" alt="Ameen" className="login-logo" />
          </div>
          <h1>مرحبًا بك في أمين</h1>
          <p className="login-subtitle">
            يرجى إدخال بياناتك للمتابعة
          </p>
        </div>

        <form className="login-form login-content" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username" className="form-label">
              اسم المستخدم
            </label>
            <input
              type="text"
              id="username"
              className={`form-input ${errors.username ? 'error' : ''}`}
              value={username}
              onChange={handleUsernameChange}
              placeholder="أدخل اسم المستخدم الخاص بك"
              disabled={isLoading}
            />
            {errors.username && (
              <span className="error-message">{errors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="mobileNumber" className="form-label">
              رقم الموبايل
            </label>
            <input
              type="tel"
              id="mobileNumber"
              className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
              value={mobileNumber}
              onChange={handleMobileChange}
              placeholder="أدخل رقم الموبايل الخاص بك"
              disabled={isLoading}
            />
            {errors.mobileNumber && (
              <span className="error-message">{errors.mobileNumber}</span>
            )}
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner"></span>
                تسجيل الدخول...
              </span>
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginScreen;
